import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  CheckCircle2,
  AlertCircle,
  Car,
  MapPin,
  Calendar,
  Clock,
  GraduationCap,
  XCircle,
  Repeat,
  Send,
} from "lucide-react";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { computeClassBreakdown, formatDateES, todayISO } from "@/lib/utils-app";

const STATUS_META = {
  pending: {
    label: "Pendiente",
    dotClass: "bg-amber-500",
    pillClass: "bg-amber-100 text-amber-800 border-amber-200",
  },
  confirmed: {
    label: "Confirmada",
    dotClass: "bg-green-500",
    pillClass: "bg-green-100 text-green-800 border-green-200",
  },
  cancelled: {
    label: "Cancelada",
    dotClass: "bg-red-500",
    pillClass: "bg-red-100 text-red-800 border-red-200",
  },
  change_requested: {
    label: "Cambio solicitado",
    dotClass: "bg-blue-500",
    pillClass: "bg-blue-100 text-blue-800 border-blue-200",
  },
};

const TYPE_INFO = {
  examen_teorico: { label: "Examen teórico", isExam: true },
  examen_practico: { label: "Examen práctico", isExam: true },
};

function typeInfo(t) {
  return TYPE_INFO[t] || { label: "Clase", isExam: false };
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border " +
        meta.pillClass
      }
    >
      <span className={"h-1.5 w-1.5 rounded-full " + meta.dotClass} />
      {meta.label}
    </span>
  );
}

function ConfirmHeader({ status, isExam = false }) {
  return (
    <div className="flex flex-col items-center mb-6" data-testid="brand-mark">
      <Logo variant="card" />
      <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mt-3">
        {isExam ? "Confirmación de examen" : "Confirmación de clase"}
      </div>
      <div className="h-px w-16 bg-neutral-200 my-4" />
      <StatusPill status={status} />
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="h-4 w-4 text-neutral-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">
          {label}
        </div>
        <div className="font-semibold text-neutral-900">{value}</div>
      </div>
    </div>
  );
}

function AppointmentDetails({ data }) {
  return (
    <div className="bg-neutral-50 rounded-xl p-4 text-left space-y-3">
      <DetailRow icon={Calendar} label="Fecha" value={formatDateES(data.date)} />
      <DetailRow
        icon={Clock}
        label="Hora"
        value={
          <span className="font-mono">
            {data.time}
            {data.end_time && <> – {data.end_time}</>}
          </span>
        }
      />
      <DetailRow icon={MapPin} label="Punto de inicio" value={data.pickup_location} />
      <DetailRow
        icon={MapPin}
        label="Punto de fin"
        value={data.pickup_location_end || data.pickup_location}
      />
      <DetailRow icon={Car} label="Ruta" value={data.circuit} />
      {data.num_classes > 0 && (
        <DetailRow
          icon={GraduationCap}
          label={`${data.num_classes} ${data.num_classes === 1 ? "clase" : "clases"} de 45 min`}
          value={
            <span className="font-mono text-xs">
              {data.classes.map((c) => `${c.index}) ${c.time}`).join("   ")}
            </span>
          }
        />
      )}
    </div>
  );
}

function ChangeRequestForm({ onSubmit, onCancel, submitting, currentDate, currentTime }) {
  const [date, setDate] = useState(currentDate || todayISO());
  const [time, setTime] = useState(currentTime || "10:00");

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ date, time });
  };

  return (
    <form
      onSubmit={submit}
      className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3"
      data-testid="change-request-form"
    >
      <div className="text-sm font-semibold text-blue-900">
        Propón una nueva fecha y hora
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-neutral-700 mb-1.5 block">
            Nueva fecha
          </label>
          <input
            data-testid="change-date-input"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full rounded-xl border border-neutral-300 bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-neutral-700 mb-1.5 block">
            Nueva hora
          </label>
          <input
            data-testid="change-time-input"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            required
            className="w-full rounded-xl border border-neutral-300 bg-white px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary flex-1"
          data-testid="change-cancel-button"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="change-submit-button"
          className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl px-4 py-2.5 transition-colors h-11 text-sm flex-1 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {submitting ? "Enviando…" : "Enviar solicitud"}
        </button>
      </div>
    </form>
  );
}

function ActionButtons({ onConfirm, onCancel, onRequestChange, busyAction, isExam = false }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-neutral-700 text-center">
        Por favor, selecciona una opción:
      </p>
      <button
        data-testid="confirm-button"
        onClick={onConfirm}
        disabled={!!busyAction}
        className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-semibold rounded-xl h-12 text-sm transition-colors"
      >
        <CheckCircle2 className="h-5 w-5" />
        {busyAction === "confirm" ? "Confirmando…" : "Confirmar asistencia"}
      </button>
      <button
        data-testid="cancel-button"
        onClick={onCancel}
        disabled={!!busyAction}
        className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold rounded-xl h-12 text-sm transition-colors"
      >
        <XCircle className="h-5 w-5" />
        {busyAction === "cancel" ? "Cancelando…" : isExam ? "Rechazar examen" : "Rechazar clase"}
      </button>
      <button
        data-testid="request-change-button"
        onClick={onRequestChange}
        disabled={!!busyAction}
        className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold rounded-xl h-12 text-sm transition-colors"
      >
        <Repeat className="h-5 w-5" />
        Solicitar cambio de horario
      </button>
    </div>
  );
}

function StatusMessage({ status, data }) {
  const info = typeInfo(data?.appointment_type);
  if (status === "confirmed") {
    return (
      <div
        data-testid="result-confirmed"
        className="bg-green-50 border border-green-200 rounded-xl p-5 text-center"
      >
        <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto mb-2" />
        <div className="text-lg font-bold text-green-800">
          {info.isExam ? `${info.label} confirmado` : "Clase confirmada"}
        </div>
        <p className="text-sm text-green-700 mt-1">
          {info.isExam ? "Te esperamos. ¡Mucha suerte!" : "Te esperamos."}
        </p>
      </div>
    );
  }
  if (status === "cancelled") {
    return (
      <div
        data-testid="result-cancelled"
        className="bg-red-50 border border-red-200 rounded-xl p-5 text-center"
      >
        <XCircle className="h-10 w-10 text-red-600 mx-auto mb-2" />
        <div className="text-lg font-bold text-red-800">
          {info.isExam ? `${info.label} rechazado` : "Clase cancelada"}
        </div>
        <p className="text-sm text-red-700 mt-1">Gracias por avisar.</p>
      </div>
    );
  }
  if (status === "change_requested") {
    return (
      <div
        data-testid="result-change-requested"
        className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-center"
      >
        <Repeat className="h-10 w-10 text-blue-600 mx-auto mb-2" />
        <div className="text-lg font-bold text-blue-800">Cambio solicitado</div>
        <p className="text-sm text-blue-700 mt-1">
          Has propuesto el{" "}
          <span className="font-mono">{formatDateES(data.requested_date)}</span>{" "}
          a las <span className="font-mono">{data.requested_time}</span>.
          <br />
          Te avisaremos cuando se confirme.
        </p>
      </div>
    );
  }
  return null;
}

export default function Confirm() {
  useDocumentTitle("Confirmación de cita");
  const [params] = useSearchParams();
  const id = params.get("id");
  const idsRaw = params.get("ids") || "";
  const viewer = params.get("viewer") || "";
  const studentParam = params.get("student") || "";
  const fromParam = params.get("from") || "";
  const ids = idsRaw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  // V1.35c · Modo bulk unificado. Preferimos `?student=X` (fuente única,
  // URL corta e incorruptible por WhatsApp). Compatibilidad con enlaces
  // antiguos `?ids=a,b,c&viewer=X`.
  if (studentParam) {
    return <ConfirmBulkByStudent studentId={studentParam} fromDate={fromParam} />;
  }
  if (ids.length > 0) {
    return <ConfirmBulk ids={ids} viewer={viewer} />;
  }

  return <ConfirmSingle id={id} />;
}

function ConfirmSingle({ id }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [showChangeForm, setShowChangeForm] = useState(false);

  const fetchAppointment = useCallback(async (appointmentId) => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/appointments/public/${appointmentId}`);
      setData(res);
      setError(null);
    } catch (err) {
      console.error("Could not load appointment:", err);
      setError(err?.response?.data?.detail || "No se pudo cargar la cita.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) {
      setError("Falta el identificador de la cita.");
      setLoading(false);
      return;
    }
    fetchAppointment(id);
  }, [id, fetchAppointment]);

  const callAction = async (action, path, body) => {
    if (!id) return;
    setBusyAction(action);
    try {
      const { data: res } = await api.post(`/appointments/public/${id}/${path}`, body);
      setData(res);
      setShowChangeForm(false);
    } catch (err) {
      console.error("Action failed:", err);
      setError(err?.response?.data?.detail || "Acción no disponible.");
    } finally {
      setBusyAction(null);
    }
  };

  const handleConfirm = () => callAction("confirm", "confirm");
  const handleCancel = () => callAction("cancel", "cancel");
  const handleChangeSubmit = ({ date, time }) =>
    callAction("change", "request-change", { date, time });

  const enriched = useMemo(() => {
    if (!data) return null;
    if (data.classes && data.classes.length > 0) return data;
    const { numClasses, classes } = computeClassBreakdown(data.time, data.end_time);
    return { ...data, num_classes: numClasses, classes };
  }, [data]);

  const status = enriched?.status || "pending";
  const isFinal =
    status === "confirmed" || status === "cancelled" || status === "change_requested";

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md card-surface p-8" data-testid="confirm-card">
        {loading && (
          <p className="text-center text-neutral-500 py-8" data-testid="confirm-loading">
            Cargando cita…
          </p>
        )}

        {!loading && error && (
          <div data-testid="confirm-error" className="text-center py-6">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">No se pudo cargar</h1>
            <p className="text-neutral-500 text-sm">{String(error)}</p>
          </div>
        )}

        {!loading && !error && enriched && (
          <>
            <ConfirmHeader status={status} isExam={typeInfo(enriched.appointment_type).isExam} />

            <div className="text-center mb-5">
              <div className="text-sm text-neutral-500">Hola</div>
              <div className="text-2xl font-bold tracking-tight">{enriched.student_name}</div>
            </div>

            <AppointmentDetails data={enriched} />

            <div className="mt-6">
              {isFinal ? (
                <StatusMessage status={status} data={enriched} />
              ) : showChangeForm ? (
                <ChangeRequestForm
                  currentDate={enriched.date}
                  currentTime={enriched.time}
                  submitting={busyAction === "change"}
                  onCancel={() => setShowChangeForm(false)}
                  onSubmit={handleChangeSubmit}
                />
              ) : (
                <ActionButtons
                  onConfirm={handleConfirm}
                  onCancel={handleCancel}
                  onRequestChange={() => setShowChangeForm(true)}
                  busyAction={busyAction}
                  isExam={typeInfo(enriched.appointment_type).isExam}
                />
              )}
            </div>
          </>
        )}
      </div>
      <Footer className="mt-4" />
    </div>
  );
}


/**
 * V1.35 · Tarjeta individual dentro del modo bulk. Encapsula el estado por
 * cita para que las acciones de una NO afecten al resto (requisito literal
 * de la constitución). Reutiliza `AppointmentDetails`, `ActionButtons` y
 * `ChangeRequestForm` — ninguna UI nueva.
 */
function BulkAppointmentCard({ item, index, onLocalConfirm }) {
  const [data, setData] = useState(item);
  const [busyAction, setBusyAction] = useState(null);
  const [showChangeForm, setShowChangeForm] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const callAction = async (action, path, body) => {
    setBusyAction(action);
    setErrorMsg(null);
    try {
      const { data: res } = await api.post(`/appointments/public/${data.id}/${path}`, body);
      setData(res);
      setShowChangeForm(false);
      if (action === "confirm" && onLocalConfirm) onLocalConfirm(data.id);
    } catch (err) {
      console.error("Bulk action failed:", err);
      setErrorMsg(err?.response?.data?.detail || "Acción no disponible.");
    } finally {
      setBusyAction(null);
    }
  };

  const enriched = useMemo(() => {
    if (!data) return null;
    if (data.classes && data.classes.length > 0) return data;
    const { numClasses, classes } = computeClassBreakdown(data.time, data.end_time);
    return { ...data, num_classes: numClasses, classes };
  }, [data]);

  const status = enriched?.status || "pending";
  const isFinal =
    status === "confirmed" || status === "cancelled" || status === "change_requested";

  return (
    <div
      className="w-full card-surface p-6 mb-4"
      data-testid={`bulk-card-${data.id}`}
      data-bulk-index={index}
    >
      <div className="flex items-center justify-between mb-3">
        <div
          className={
            "text-[11px] uppercase tracking-[0.18em] font-semibold " +
            (typeInfo(data?.appointment_type).isExam ? "text-purple-700" : "text-neutral-500")
          }
          data-testid={`bulk-card-type-${index}`}
        >
          {typeInfo(data?.appointment_type).isExam
            ? typeInfo(data.appointment_type).label
            : `Sesión ${index}`}
        </div>
        <StatusPill status={status} />
      </div>

      <AppointmentDetails data={enriched} />

      {errorMsg && (
        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {errorMsg}
        </div>
      )}

      <div className="mt-5">
        {isFinal ? (
          <StatusMessage status={status} data={enriched} />
        ) : showChangeForm ? (
          <ChangeRequestForm
            currentDate={enriched.date}
            currentTime={enriched.time}
            submitting={busyAction === "change"}
            onCancel={() => setShowChangeForm(false)}
            onSubmit={({ date, time }) => callAction("change", "request-change", { date, time })}
          />
        ) : (
          <ActionButtons
            onConfirm={() => callAction("confirm", "confirm")}
            onCancel={() => callAction("cancel", "cancel")}
            onRequestChange={() => setShowChangeForm(true)}
            busyAction={busyAction}
            isExam={typeInfo(data?.appointment_type).isExam}
          />
        )}
      </div>
    </div>
  );
}


/**
 * V1.35c · ORDEN DE EMERGENCIA — fuente de datos ÚNICA para la vista pública.
 * El enlace WhatsApp incluye `?student=<sid>` (corto, no lo puede corromper
 * el copia-pega ni el truncado de WhatsApp). Este componente consulta el
 * mismo backend que utiliza `BulkWhatsAppDialog`, garantizando que el alumno
 * vea EXACTAMENTE las mismas N citas que el profesor programa.
 */
function ConfirmBulkByStudent({ studentId, fromDate }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/appointments/public-by-student`, {
          params: {
            student_id: studentId,
            ...(fromDate ? { from_date: fromDate } : {}),
          },
        });
        if (cancelled) return;
        setItems(Array.isArray(data?.appointments) ? data.appointments : []);
        setError(null);
      } catch (err) {
        console.error("public-by-student fetch failed:", err);
        if (!cancelled) {
          setError(err?.response?.data?.detail || "No se pudieron cargar las citas.");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId, fromDate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center px-4 py-12">
        <p className="text-center text-neutral-500 py-8" data-testid="confirm-loading">
          Cargando citas…
        </p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-md card-surface p-8 text-center" data-testid="confirm-error">
          <div className="mx-auto h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">No se pudo cargar</h1>
          <p className="text-neutral-500 text-sm">{String(error)}</p>
        </div>
      </div>
    );
  }
  return <ConfirmBulk ids={[]} viewer={studentId} preloadedItems={items} />;
}


function ConfirmBulk({ ids, viewer, preloadedItems = null, sourceLabel = null }) {
  const [loading, setLoading] = useState(preloadedItems === null);
  const [error, setError] = useState(null);
  const [items, setItems] = useState(preloadedItems || []);
  const [confirmingAll, setConfirmingAll] = useState(false);
  // Un contador para forzar el re-render de los hijos cuando "Confirmar todas"
  // completa: cada BulkAppointmentCard es autónoma, así que la única forma
  // de sincronizarlos es reconstruirlos con una key nueva.
  const [confirmAllTick, setConfirmAllTick] = useState(0);

  useEffect(() => {
    // Si el padre nos precarga los items (modo student=), no hacemos fetch
    if (preloadedItems !== null) {
      setItems(preloadedItems);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/appointments/public-bulk`, {
          params: { ids: ids.join(","), viewer: viewer || undefined },
        });
        if (!cancelled) {
          setItems(Array.isArray(data?.appointments) ? data.appointments : []);
          setError(null);
        }
      } catch (err) {
        console.error("Bulk fetch failed:", err);
        if (!cancelled) {
          setError(err?.response?.data?.detail || "No se pudieron cargar las citas.");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ids.join(","), viewer, preloadedItems]);

  const studentName = items[0]?.student_name || "";

  const pendingIds = useMemo(
    () =>
      items
        .filter((a) => a.status !== "confirmed" && a.status !== "cancelled" && a.status !== "change_requested")
        .map((a) => a.id),
    [items],
  );

  const handleConfirmAll = async () => {
    if (pendingIds.length === 0 || confirmingAll) return;
    setConfirmingAll(true);
    try {
      const results = await Promise.all(
        pendingIds.map((aid) =>
          api
            .post(`/appointments/public/${aid}/confirm`)
            .then((r) => ({ id: aid, ok: true, data: r.data }))
            .catch((e) => ({ id: aid, ok: false, err: e })),
        ),
      );
      // Actualizar cada item con la nueva versión
      const byId = new Map(results.map((r) => [r.id, r]));
      setItems((prev) =>
        prev.map((it) => {
          const r = byId.get(it.id);
          if (r && r.ok && r.data) return r.data;
          return it;
        }),
      );
      setConfirmAllTick((t) => t + 1);
    } finally {
      setConfirmingAll(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col items-center px-4 py-12">
      <div className="w-full max-w-md" data-testid="bulk-confirm-root">
        <div className="card-surface p-6 mb-4" data-testid="bulk-header-card">
          <ConfirmHeader status="pending" />
          <div className="text-center">
            <div className="text-sm text-neutral-500">Hola</div>
            <div className="text-2xl font-bold tracking-tight" data-testid="bulk-student-name">
              {studentName || "—"}
            </div>
            {items.length > 0 && (
              <div className="text-xs text-neutral-500 mt-1" data-testid="bulk-count">
                {items.length} {items.length === 1 ? "sesión" : "sesiones"} pendientes de tu respuesta
              </div>
            )}
          </div>
        </div>

        {loading && (
          <p className="text-center text-neutral-500 py-8" data-testid="confirm-loading">
            Cargando citas…
          </p>
        )}

        {!loading && error && (
          <div data-testid="confirm-error" className="card-surface p-6 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">No se pudieron cargar</h1>
            <p className="text-neutral-500 text-sm">{String(error)}</p>
          </div>
        )}

        {!loading && !error && items.map((it, idx) => (
          <BulkAppointmentCard
            key={`${it.id}-${confirmAllTick}`}
            item={it}
            index={idx + 1}
          />
        ))}

        {!loading && !error && pendingIds.length > 1 && (
          <button
            type="button"
            onClick={handleConfirmAll}
            disabled={confirmingAll}
            data-testid="bulk-confirm-all"
            className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-semibold rounded-xl h-12 text-sm transition-colors mt-2"
          >
            <CheckCircle2 className="h-5 w-5" />
            {confirmingAll
              ? "Confirmando…"
              : `Confirmar todas las sesiones (${pendingIds.length})`}
          </button>
        )}
      </div>
      <Footer className="mt-4" />
    </div>
  );
}
